import { mapActions, mapState } from 'vuex'
import { equals, isNil, ap } from 'ramda'
import { concatRight } from 'ramda-adjunct'
import { GET, UPDATE, CREATE, DELETE } from 'constants/api'
import * as DEFAULT_API_PARAMS from 'constants/defaultApiParams'
import { getCommonFormSubmit, showErrorWithTimeout } from 'utils/form'
import { getModuleName, toSingular, toPlural } from 'utils/helpers'
import { showDeleteDialog } from '../utils/dialog'
import apiMessagesMixin from './apiMessagesMixin'

const allApiMethods = [GET, UPDATE, CREATE, DELETE]

export default ({
  apiMethodsSuffix,
  parentRouteName,
  storeModuleName,
  storeItemName,
  enums,
  enumsMap,
  allowedApiMethods = allApiMethods
}) => {
  const moduleName = isNil(storeModuleName)
    ? getModuleName({ apiMethodsSuffix })
    : storeModuleName

  const itemName = isNil(storeItemName)
    ? toSingular(moduleName)
    : storeItemName

  const moduleNameUppercase = moduleName.toUpperCase()

  const defaultParams = DEFAULT_API_PARAMS[moduleNameUppercase] || {}

  const stateToMap = {
    item(state) {
      const item = state[moduleName][itemName]
      const itemId = this.itemId

      return isNil(item) || isNil(item._id) || isNil(itemId) || equals(item._id && item._id.toString(), itemId.toString()) ? item : {}
    },
    pending: state => state[moduleName].pending[itemName]
  }

  if (enums) {
    stateToMap.enums = state => state.enums.enums
  }

  if (enumsMap) {
    stateToMap.enumsMap = state => state.enums.enumsMap
  }

  return {
    mixins: [
      apiMessagesMixin
    ],
    created() {
      this.initParams()
      this.fetchDetail()
    },
    data() {
      return {
        backAllowed: false,
        showBack: false,
        apiErrors: {},
        params: {
          ...defaultParams
        }
      }
    },
    computed: {
      ...mapState({
        ...stateToMap,
        currentLanguage: state => state.uiLanguages.currentLanguage,
        savedParams: state => state.layout.params[moduleNameUppercase]
      }),
      newItem() {
        return this.itemId === 'new'
      },
      itemId() {
        return this.$route.params.id
      },
      backRoute() {
        return isNil(parentRouteName) ? toPlural(this.$route.name) : parentRouteName
      },
      formItem() {
        return JSON.parse(JSON.stringify(this.item))
      }
    },
    methods: {
      ...mapActions(
        [
          ...ap(
            [concatRight(apiMethodsSuffix)],
            allowedApiMethods
          ),
          'appShowErrors',
          'appClearErrors',
          'setParams'
        ]
      ),
      fetchDetail() {
        const id = this.itemId

        if (!this.newItem) {
          this[GET + apiMethodsSuffix]({
            params: {
              id: id,
              idLanguage: localStorage.ui_language_id
            }
          })
        }
      },
      allowShowBack() {
        this.showBack = true
        this.backAllowed = true
      },
      allowBack() {
        this.backAllowed = true
      },
      disallowBack() {
        this.backAllowed = false
      },
      async deleteItem() {
        const deleteError = this.getDeleteError(this.item)
        if (deleteError) {
          showErrorWithTimeout(this, deleteError)
        } else if (this.shouldConfirmDelete(this.item)) {
          showDeleteDialog(this, {
            apiMethod: DELETE + apiMethodsSuffix,
            backRoute: this.backRoute,
            params: {
              id: this.itemId
            }
          }, this.getDeleteMessage(this.item))
        } else {
          await this[DELETE + apiMethodsSuffix]({ params: { id: this.item._id } })
          this.goBack()
        }
      },
      getDeleteMessage() {
        return null
      },
      getDeleteError() {
        return null
      },
      shouldConfirmDelete() {
        return true
      },
      async submitForm(e) {
        this.errors.clear()

        await this.$validator.validateAll()
        if (this.errors.items.length > 0) {
          this.appShowErrors(this.errors.items.map(v => v.msg))
          return
        }
        const commonFormSubmit = getCommonFormSubmit(apiMethodsSuffix)
        const response = await commonFormSubmit(this, e).catch(err => err.response)

        this.handleResponseIfError(response)

        if (!this.backAllowed && response.status === 201) {
          this.goToDetail(response.data.data._id)
        }
      },
      handleResponseIfError(response) {
        if (response.status === 500) {
          this.appShowErrors([this.errorTrans.serverError])
        }

        if (response.status === 422) {
          this.apiErrors = response.data.error

          if (this.apiErrors && this.apiErrors.length) {
            this.mapApiErrorsToErrors()
            this.appShowErrors([this.errorTrans.formError])
          }

          const regex = RegExp('isoCodeMustBeUnique')

          if (regex.test(response.data.message)) {
            this.errors.add({ field: 'isoCode', msg: this.apiMessagesMap.isoCodeMustBeUnique })
            this.appShowErrors([this.errorTrans.formError])
          } else {
            let stringError = response.data.message
            if (response.data.message.indexOf('Model errors:') >= 0) {
              stringError = response.data.message.replace('Model errors: ', '')
            }

            this.appShowErrors([this.apiMessagesMap[stringError] || stringError])
          }
        }
      },
      goToDetail(id) {
        this.$router.push({
          params: {
            id
          }
        })
      },
      goBack() {
        if (this.showBack && this.backAllowed) {
          this.$router.back()
        } else {
          this.$router.push({
            name: this.backRoute
          })
        }
      },
      mapApiErrorsToErrors() {
        this.errors.add(this.apiErrors.map(v => ({ field: v.path, msg: this.translateMsg(this.getErrorMessage(v.message)), invalidValue: v.invalidValue, inputField: this.getErrorField(v.message) })))
      },
      getErrorMessage(msg) {
        if (msg.includes('-')) {
          const newMsg = msg.split('-')
          newMsg.shift()

          return newMsg.join('-')
        }

        return msg
      },
      getErrorField(msg) {
        if (msg.includes('-')) {
          return msg.split('-')[0]
        }

        return null
      },
      initParams() {
        if (this.savedParams) {
          this.params = this.savedParams
        }
        this.saveParams()
      },
      saveParams() {
        this.setParams({
          agendaParams: { ...this.params },
          agendaName: moduleNameUppercase
        })
      },
      onAppTabChanged(v) {
        this.params.tab = v
      }
    },
    watch: {
      currentLanguage() {
        if (this.apiErrors && this.apiErrors.length) {
          this.errors.clear()
          this.mapApiErrorsToErrors()
          this.appClearErrors()
        }
      },
      params: {
        handler() {
          this.saveParams()
        },
        deep: true
      }
    }
  }
}
